import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  brandExtractionApi,
  type BrandKitProposal,
  type AcceptedBrandKit,
  ApiError,
} from '../api/client';

type Stage =
  | { kind: 'select-source' }
  | { kind: 'extracting' }
  | { kind: 'review'; proposal: BrandKitProposal; sourceType: 'URL' | 'PDF'; sourceRef: string }
  | { kind: 'accepting' }
  | { kind: 'done' }
  | { kind: 'error'; message: string };

export function BrandExtractionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>({ kind: 'select-source' });

  if (!id) return null;

  if (stage.kind === 'select-source') {
    return <SourceSelector tenantId={id} onResult={(r) => setStage({ kind: 'review', proposal: r.proposal, sourceType: r.sourceType, sourceRef: r.sourceRef })} onError={(message) => setStage({ kind: 'error', message })} setStage={setStage} />;
  }
  if (stage.kind === 'extracting') {
    return <Layout><Spinner label="Asking Claude to read the source — usually 5–15 seconds…" /></Layout>;
  }
  if (stage.kind === 'review') {
    return (
      <ReviewForm
        tenantId={id}
        initial={stage.proposal}
        sourceType={stage.sourceType}
        sourceRef={stage.sourceRef}
        onAccepting={() => setStage({ kind: 'accepting' })}
        onAccepted={() => setStage({ kind: 'done' })}
        onError={(message) => setStage({ kind: 'error', message })}
      />
    );
  }
  if (stage.kind === 'accepting') {
    return <Layout><Spinner label="Saving brand kit…" /></Layout>;
  }
  if (stage.kind === 'done') {
    return (
      <Layout>
        <div className="rounded-2xl bg-good/15 p-6 space-y-3">
          <p className="text-good font-medium">Brand kit saved.</p>
          <button
            onClick={() => navigate(`/tenants/${id}`)}
            className="text-sm text-ink underline-offset-4 hover:underline"
          >
            ← Back to tenant
          </button>
        </div>
      </Layout>
    );
  }
  return (
    <Layout>
      <div className="rounded-2xl bg-bad/15 p-6 space-y-3">
        <p className="text-bad font-medium">{stage.message}</p>
        <button
          onClick={() => setStage({ kind: 'select-source' })}
          className="text-sm text-ink underline-offset-4 hover:underline"
        >
          ← Try again
        </button>
      </div>
    </Layout>
  );
}

// ─── Source selector ─────────────────────────────────────────────────────

function SourceSelector({
  tenantId, onResult, onError, setStage,
}: {
  tenantId: string;
  onResult: (r: { proposal: BrandKitProposal; sourceType: 'URL' | 'PDF'; sourceRef: string }) => void;
  onError: (message: string) => void;
  setStage: (s: Stage) => void;
}) {
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);

  async function submitUrl(e: FormEvent) {
    e.preventDefault();
    setStage({ kind: 'extracting' });
    try {
      const r = await brandExtractionApi.fromUrl(tenantId, url.trim());
      onResult({ proposal: r.proposal, sourceType: r.source_type, sourceRef: r.source_reference });
    } catch (err) {
      onError(err instanceof ApiError ? err.body.message ?? err.body.error ?? 'Extraction failed' : 'Extraction failed');
    }
  }

  async function submitPdf(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    setStage({ kind: 'extracting' });
    try {
      const r = await brandExtractionApi.fromPdf(tenantId, file);
      onResult({ proposal: r.proposal, sourceType: r.source_type, sourceRef: r.source_reference });
    } catch (err) {
      onError(err instanceof ApiError ? err.body.message ?? err.body.error ?? 'Extraction failed' : 'Extraction failed');
    }
  }

  return (
    <Layout>
      <p className="text-sm text-ink-muted">
        Provide a source — Aurem will use Claude to extract a brand kit (palette + typography +
        default colour mode + tone). You'll review + edit the proposal before saving.
      </p>

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <h2 className="font-heading text-lg">From a website URL</h2>
        <form onSubmit={submitUrl} className="space-y-3">
          <input
            type="url"
            required
            placeholder="https://livemore.health"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
          />
          <button
            type="submit"
            disabled={!url}
            className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-ink transition disabled:opacity-50"
          >
            Extract from URL
          </button>
        </form>
      </section>

      <section className="rounded-2xl bg-surface-2 p-6 space-y-4">
        <h2 className="font-heading text-lg">From a PDF</h2>
        <form onSubmit={submitPdf} className="space-y-3">
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-ink-muted file:mr-3 file:rounded-md file:border-0 file:bg-surface-3 file:px-3 file:py-2 file:text-ink file:cursor-pointer"
          />
          <button
            type="submit"
            disabled={!file}
            className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-ink transition disabled:opacity-50"
          >
            Extract from PDF
          </button>
        </form>
      </section>
    </Layout>
  );
}

// ─── Review form ─────────────────────────────────────────────────────────

function ReviewForm({
  tenantId, initial, sourceType, sourceRef, onAccepting, onAccepted, onError,
}: {
  tenantId: string;
  initial: BrandKitProposal;
  sourceType: 'URL' | 'PDF';
  sourceRef: string;
  onAccepting: () => void;
  onAccepted: () => void;
  onError: (message: string) => void;
}) {
  const [fields, setFields] = useState<AcceptedBrandKit>({
    brand_primary_color: initial.primary_color,
    brand_secondary_color: initial.secondary_color ?? null,
    brand_accent_color: initial.accent_color ?? null,
    brand_font_heading: initial.font_heading ?? null,
    brand_font_body: initial.font_body ?? null,
    brand_default_color_mode: initial.default_color_mode,
  });

  function update<K extends keyof AcceptedBrandKit>(key: K, value: AcceptedBrandKit[K]) {
    setFields((f) => ({ ...f, [key]: value }));
  }

  async function onAccept(e: FormEvent) {
    e.preventDefault();
    onAccepting();
    try {
      await brandExtractionApi.accept(tenantId, fields);
      onAccepted();
    } catch (err) {
      onError(err instanceof ApiError ? err.body.message ?? err.body.error ?? 'Save failed' : 'Save failed');
    }
  }

  return (
    <Layout>
      <p className="text-sm text-ink-muted">
        Source: <span className="font-mono text-ink">{sourceType}</span>{' '}
        — <span className="font-mono text-xs text-ink-muted">{sourceRef}</span>
      </p>

      {initial.tone_hints && (
        <section className="rounded-2xl bg-surface-2 p-4 text-sm text-ink-muted">
          Tone read: <span className="text-ink">{initial.tone_hints}</span>
        </section>
      )}

      {initial.logo_description && (
        <section className="rounded-2xl bg-surface-2 p-4 text-sm text-ink-muted">
          Logo notes: <span className="text-ink">{initial.logo_description}</span>
          <p className="text-xs mt-1">
            (The actual logo file is uploaded manually — Phase 0.5 ships R2-upload UI.)
          </p>
        </section>
      )}

      {initial.decision_points && initial.decision_points.length > 0 && (
        <section className="rounded-2xl bg-warn/10 border border-warn/30 p-6 space-y-4">
          <h3 className="font-heading text-base text-warn">Claude flagged some questions</h3>
          {initial.decision_points.map((dp, i) => (
            <div key={i} className="space-y-1 text-sm">
              <p className="text-ink font-medium">{dp.question}</p>
              {dp.context && <p className="text-ink-muted">{dp.context}</p>}
              {dp.ai_recommendation && <p className="text-ink-muted">AI suggestion: {dp.ai_recommendation}</p>}
              {dp.options && dp.options.length > 0 && (
                <ul className="list-disc list-inside text-ink-muted">
                  {dp.options.map((o, j) => <li key={j}>{o}</li>)}
                </ul>
              )}
            </div>
          ))}
          <p className="text-xs text-ink-muted">Resolve by adjusting the fields below.</p>
        </section>
      )}

      <form onSubmit={onAccept} className="rounded-2xl bg-surface-2 p-6 space-y-5">
        <h2 className="font-heading text-lg">Brand kit</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ColorField label="Primary" required value={fields.brand_primary_color} onChange={(v) => update('brand_primary_color', v)} />
          <ColorField label="Secondary" value={fields.brand_secondary_color ?? ''} onChange={(v) => update('brand_secondary_color', v || null)} />
          <ColorField label="Accent" value={fields.brand_accent_color ?? ''} onChange={(v) => update('brand_accent_color', v || null)} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Heading font (Google Fonts name)">
            <input
              type="text"
              value={fields.brand_font_heading ?? ''}
              onChange={(e) => update('brand_font_heading', e.target.value || null)}
              placeholder="Inter"
              className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
            />
          </Field>
          <Field label="Body font (Google Fonts name)">
            <input
              type="text"
              value={fields.brand_font_body ?? ''}
              onChange={(e) => update('brand_font_body', e.target.value || null)}
              placeholder="Inter"
              className="w-full rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink outline-none ring-1 ring-transparent focus:ring-primary"
            />
          </Field>
        </div>

        <Field label="Default colour mode">
          <div className="flex gap-2">
            {(['DARK', 'LIGHT'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => update('brand_default_color_mode', mode)}
                className={`rounded-lg px-4 py-2 text-sm transition ${
                  fields.brand_default_color_mode === mode
                    ? 'bg-primary text-ink'
                    : 'bg-surface-3 text-ink-muted hover:text-ink'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        </Field>

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            className="rounded-lg bg-primary px-4 py-2.5 font-medium text-ink"
          >
            Accept and save
          </button>
          <Link
            to={`/tenants/${tenantId}`}
            className="rounded-lg bg-surface-3 px-4 py-2.5 font-medium text-ink-muted hover:text-ink transition"
          >
            Cancel
          </Link>
        </div>
      </form>
    </Layout>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  const { id } = useParams();
  return (
    <div className="space-y-6">
      <Link to={`/tenants/${id}`} className="text-xs text-ink-muted hover:text-ink uppercase tracking-wider">
        ← Tenant
      </Link>
      <h1 className="font-heading text-2xl font-semibold tracking-tight">Brand identity extraction</h1>
      {children}
    </div>
  );
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-ink-muted mb-1.5">
        {label}{required ? ' *' : ''}
      </span>
      {children}
    </label>
  );
}

function ColorField({ label, value, onChange, required }: { label: string; value: string; onChange: (v: string) => void; required?: boolean }) {
  return (
    <Field label={label} required={required}>
      <div className="flex gap-2">
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-10 rounded-md border border-surface-3 bg-transparent cursor-pointer"
        />
        <input
          type="text"
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#B8860B"
          pattern="#[0-9a-fA-F]{6}"
          className="flex-1 rounded-lg bg-surface-3 px-3.5 py-2.5 text-ink font-mono outline-none ring-1 ring-transparent focus:ring-primary"
        />
      </div>
    </Field>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className="rounded-2xl bg-surface-2 p-8 text-center space-y-3">
      <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-ink-muted border-t-primary" />
      <p className="text-sm text-ink-muted">{label}</p>
    </div>
  );
}
